import clsx from 'clsx'
import PropTypes from 'prop-types'

import useAttentionPulse, { PULS_KLASSE } from '../../shared/useAttentionPulse'

import styles from './style.module.scss'

/**
 * Ein waagrechter Trichter als Filter.
 *
 * Tritt an die Stelle der frueheren Status-Chips: Er filtert genauso (eine
 * Stufe anklicken schaltet den Status an/aus, mehrere sind moeglich), zeigt
 * aber zusaetzlich die Anzahl je Stufe und den Gesamtstand. Die Zahlen kommen
 * vom Server (`statusCounts`) und bleiben stehen, waehrend man filtert - so
 * sieht man immer, wohin sich ein Wechsel lohnt.
 */
export default function StatusFunnel({ counts, secondary, value, onChange, options, hints, label, single }) {
	const active = value || []
	// Aktive Stufen leuchten beim Anzeigen kurz auf: Eine gewaehlte Stufe faerbt
	// nur einen von acht Kaesten ein — beim Ueberfliegen sieht die Leiste aus wie
	// eine Uebersicht und nicht wie ein gesetzter Filter.
	const puls = useAttentionPulse(active.length > 0)
	/*
	 * Der Gesamtstand ist die Summe der Stufen — richtig, solange jede Zeile in
	 * genau einer Stufe steht (Statusfilter). Bauen die Stufen dagegen
	 * aufeinander auf (`single`), zaehlt dieselbe Zeile mehrfach mit, und die
	 * Summe ist keine Menge, die es gibt: Beim Konzept stand dort „202", waehrend
	 * die Auswertung 5029 Schueler:innen fuehrt. Dann bleibt die Zahl weg.
	 */
	const total = single ? null : options.reduce((sum, o) => sum + (counts[o.value] || 0), 0)
	// Die Balkenhöhe ist relativ zur größten Stufe - so ist auf einen Blick
	// erkennbar, wo sich die Anforderungen stauen. Mindestens 1, damit die
	// Division nie durch 0 geht, wenn (noch) nichts gezählt wurde.
	const max = Math.max(1, ...options.map(o => counts[o.value] || 0))

	// Die Beschreibungszeile folgt der zuletzt gewaehlten Stufe, sonst der ersten.
	const focus = active.length ? active[active.length - 1] : options[0].value
	const focusOption = options.find(o => o.value === focus)

	/*
	 * Zwei Betriebsarten. Beim Statusfilter sind mehrere Stufen gleichzeitig
	 * moeglich — man will „neu und in Klaerung" nebeneinander sehen. Bei einem
	 * Trichter, dessen Stufen aufeinander aufbauen, waere das sinnlos: Die
	 * spaetere Stufe ist immer eine Teilmenge der frueheren. Dort waehlt ein
	 * Klick genau eine Stufe, ein zweiter Klick hebt sie wieder auf.
	 */
	const toggle = statusValue => {
		if (single) return onChange(active.includes(statusValue) ? [] : [statusValue])

		const next = active.includes(statusValue)
			? active.filter(item => item !== statusValue)
			: [...active, statusValue]
		onChange(next)
	}

	return (
		<div className={styles.funnel} role="group" aria-label={label}>
			<div className={styles.track}>
				{total !== null && (
					<div className={styles.stand}>
						<span className={styles.standLabel}>Stand</span>
						<span className={styles.standValue}>{total}</span>
					</div>
				)}

				{options.map((option, index) => {
					const isActive = active.includes(option.value)
					const dimmed = active.length > 0 && !isActive
					const count = counts[option.value] || 0
					// Nicht-null immer als sichtbarer Sockel (min. 8 %), sonst gingen
					// kleine Stufen neben einer großen optisch unter.
					const barHeight = count === 0 ? 0 : Math.max(8, Math.round((count / max) * 100))

					return (
						<div key={option.value} className={styles.segmentWrap}>
							{index > 0 && (
								<span className={styles.chevron} aria-hidden="true">
									›
								</span>
							)}
							<button
								type="button"
								aria-pressed={isActive}
								onAnimationEnd={puls.onAnimationEnd}
								className={clsx(
									styles.segment,
									styles[option.value],
									isActive && styles.active,
									isActive && puls.leuchtet && PULS_KLASSE,
									dimmed && styles.dimmed
								)}
								onClick={() => toggle(option.value)}
							>
								<span className={styles.segLabel}>{option.label}</span>
								<span className={styles.segCount}>{count}</span>
								{secondary?.[option.value] !== undefined && (
									<span className={styles.segSecond}>{secondary[option.value]}</span>
								)}
								<span className={styles.barTrack}>
									<span className={styles.barFill} style={{ height: `${barHeight}%` }} />
								</span>
							</button>
						</div>
					)
				})}
			</div>

			{focusOption && (
				<p className={styles.legend}>
					<strong>{focusOption.label}:</strong> {hints[focus]}
				</p>
			)}
		</div>
	)
}

StatusFunnel.propTypes = {
	// { NEW: 38, CLARIFICATION: 0, ... } - fehlende Stufen zaehlen als 0.
	counts: PropTypes.object,
	// Eine zweite, kleinere Zahl unter der Hauptzahl. Der Trichter des
	// Konzepts zaehlt Personen und Junior Companies nebeneinander; zwei
	// Trichter uebereinander waeren dieselbe Aussage doppelt.
	secondary: PropTypes.object,
	value: PropTypes.array,
	onChange: PropTypes.func.isRequired,
	// [{ value, label }] — die Farbe kommt aus einer Klasse gleichen Namens im
	// Stylesheet; fehlt sie, greift der neutrale Standardton.
	options: PropTypes.array.isRequired,
	hints: PropTypes.object,
	label: PropTypes.string,
	single: PropTypes.bool
}

StatusFunnel.defaultProps = {
	counts: {},
	secondary: null,
	value: [],
	hints: {},
	label: 'Filter',
	single: false
}
